import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toolbarButtons } from '../../config/orderStatusConfig';
import { SelectionToolbar } from './SelectionToolbar';

import { BulkPreparationModal, BulkPreparationModalRef } from '../modals/BulkPreparationModal';

type BulkActionResult = Promise<boolean> | boolean | void;

/** Contadores de pedidos por status_id */
export type StatusCounts = Record<string, number>;

export interface StatusToolbarProps {
  onStatusChange?: (status_id?: number) => void;
  selectedCount?: number;
  onClearSelection?: () => void;
  onCancelSelected?: () => BulkActionResult;
  onPrepareSelected?: (preparationType: 'picking_packing' | 'massive_packing') => BulkActionResult;
  onMarkForSendSelected?: () => BulkActionResult;
  onUncancelSelected?: () => BulkActionResult;
  cancelableCount?: number;
  preparableCount?: number;
  sendableCount?: number;
  uncancelableCount?: number;
  /** Contadores de pedidos por status */
  statusCounts?: StatusCounts;
  /** Si está cargando los contadores */
  isLoadingCounts?: boolean;
}

export const StatusToolbar = ({
  onStatusChange,
  selectedCount = 0,
  onClearSelection,
  onCancelSelected,
  onPrepareSelected,
  onMarkForSendSelected,
  onUncancelSelected,
  cancelableCount = 0,
  preparableCount = 0,
  sendableCount = 0,
  uncancelableCount = 0,
  statusCounts = {},
  isLoadingCounts = false,
  active,
  setActive,
}: StatusToolbarProps & {
  active: string;
  setActive: (key: string) => void;
}) => {
  const { t } = useTranslation('ordersModule');
  const bulkPreparationModalRef = useRef<BulkPreparationModalRef>(null);

  // Calcular el total para "Todos"
  const totalCount = useMemo(() => {
    return Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
  }, [statusCounts]);

  // Obtener el conteo para un status_id específico
  const getCountForStatus = (statusId?: number): number | undefined => {
    if (statusId === undefined) {
      // "Todos" - mostrar total
      return totalCount;
    }
    // En preparación: mismo criterio que el filtro del listado (status_ids [4, 5])
    if (statusId === 4) {
      return (statusCounts['4'] ?? 0) + (statusCounts['5'] ?? 0);
    }
    return statusCounts[String(statusId)];
  };

  // Formatear el conteo para mostrar
  const formatCount = (count: number | undefined): string => {
    if (count === undefined) return '';
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return String(count);
  };

  const handlePrepareSelected = () => {
    if (preparableCount > 0) {
      bulkPreparationModalRef.current?.open(preparableCount);
    }
  };

  const handleBulkPreparationConfirm = (preparationType: 'picking_packing' | 'massive_packing') => {
    onPrepareSelected?.(preparationType);
  };

  // Barra flotante solo en mobile y solo si hay selección
  const showSelectionToolbar = selectedCount > 0;
  const isMobileView = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  return (
    <>
      {/* Mobile: barra flotante */}
      {showSelectionToolbar && isMobileView && (
        <div>
          <SelectionToolbar
            selectedCount={selectedCount}
            onClose={onClearSelection || (() => {})}
            onCancelSelected={onCancelSelected || (() => {})}
            onPrepareSelected={handlePrepareSelected}
            onMarkForSendSelected={onMarkForSendSelected || (() => {})}
            onUncancelSelected={onUncancelSelected || (() => {})}
            cancelableCount={cancelableCount}
            preparableCount={preparableCount}
            sendableCount={sendableCount}
            uncancelableCount={uncancelableCount}
          />
        </div>
      )}

      {/* Desktop: barra de estados siempre visible */}
      <div
        className="flex bg-card-bg rounded-lg text-sm p-1 mb-4 overflow-x-auto scrollbar-hide light:border-card-border light:border"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {toolbarButtons.map((btn) => {
          const count = getCountForStatus(btn.status_id);
          const countText = formatCount(count);

          return (
            <a
              key={btn.key}
              href={btn.link}
              onClick={(e) => {
                e.preventDefault();
                setActive(btn.key);
                if (onStatusChange) {
                  onStatusChange(btn.status_id);
                }
              }}
              className={`px-3 py-1 rounded hover:bg-hover-press whitespace-nowrap flex items-center gap-1.5 ${active === btn.key ? 'bg-hover-press' : ''}`}
            >
              <span>{t(`toolbar.${btn.key.replace('in_preparation2', 'in_preparation')}`)}</span>
              {countText && (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
                    active === btn.key ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                  } ${isLoadingCounts ? 'animate-pulse' : ''}`}
                >
                  {countText}
                </span>
              )}
            </a>
          );
        })}
      </div>

      <BulkPreparationModal ref={bulkPreparationModalRef} onConfirm={handleBulkPreparationConfirm} />
    </>
  );
};
